<template>
    <main class="flex-1 relative overflow-y-auto py-6 focus:outline-none" tabindex="0">
        
            <div class="max-w-7xl mx-auto sm:px-6 lg:px-8 px-5">
                <div class="md:flex md:items-center md:justify-between">
                    <div class="flex-1 min-w-0">
                        <h1 class="py-0.5 text-2xl font-semibold text-gray-900">{{ $t('Create ticket') }}</h1>
                    </div>
                </div>
            </div>
            <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div class="mt-6 shadow sm:rounded-lg">
                    <loading :status="loading.form"/>
                    <div class="bg-white md:grid md:grid-cols-3 md:gap-6 px-4 py-5 sm:p-6">
                        <div class="md:col-span-1">
                            <h3 class="text-lg font-medium leading-6 text-gray-900">{{ $t('Ticket details') }}</h3>
                            <p class="mt-1 text-sm leading-5 text-gray-500">
                                {{ $t('Ticket details and classification') }}.
                            </p>
                        </div>
                        <div class="mt-5 md:mt-0 md:col-span-2">
                            <form id="searchCustomer" class="mb-5" @submit.prevent="searchCustomer">
                                <div class="grid grid-cols-3 gap-6 rounded shadow-md p-4 bg-white">
                                    <div class="col-span-3 flex">
                                        <div class="flex items-center mr-3">
                                            <input
                                                id="searchType1"
                                                type="radio"
                                                v-model="search.type"
                                                value="nomenclature"
                                                class="w-4 text-blue-600 focus:ring-blue-500 border-gray-300 mr-1"
                                            />
                                            <label class="text-sm font-medium leading-5 text-gray-700" for="searchType1">{{ $t('Nomenclature') }}</label>
                                        </div>
                                        <div class="flex items-center">
                                            <input
                                                id="searchType2"
                                                type="radio"
                                                v-model="search.type"
                                                value="board"
                                                class="w-4 text-blue-600 focus:ring-blue-500 border-gray-300 mr-1"
                                            />
                                            <label class="text-sm font-medium leading-5 text-gray-700" for="searchType2">{{ $t('Board') }}</label>
                                        </div>
                                        <div v-if="errors.type" class="col-span-3">
                                            <p class="text-red-600 text-sm mt-2">{{ errors.type }}</p>
                                        </div>
                                    </div>
                                    <div v-if="search.type === 'board'" class="col-span-3">
                                        <label class="block text-sm font-medium leading-5 text-gray-700" for="mapurl">{{ $t('Map url') }}</label>
                                        <div class="mt-1 relative rounded-md shadow-sm">
                                            <input
                                                id="mapurl"
                                                v-model="search.mapUrl"
                                                :placeholder="$t('Map url')"
                                                class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                                reqired
                                            >
                                        </div>
                                        <div v-if="errors.mapUrl" class="col-span-3">
                                            <p class="text-red-600 text-sm mt-2">{{ errors.mapUrl }}</p>
                                        </div>
                                    </div>
                                    <div class="col-span-3">
                                        <label class="block text-sm font-medium leading-5 text-gray-700" for="customerNeighborhood">{{ $t('Ticket neighborhood') }}</label>
                                        <div class="mt-1 relative rounded-md shadow-sm">
                                            <input-select
                                                id="customerNeighborhood"
                                                v-model="search.neighborhood_id"
                                                :options="neighborhoodList"
                                                option-label="name"
                                                searchable
                                            >
                                                <!-- Plantilla para la opción seleccionada -->
                                                <template v-slot:selectedOption="props">
                                                    <template v-if="props && !props.anySelected">
                                                        <span class="block truncate">{{ $t('Select an option') }}</span>
                                                    </template>
                                                    <template v-else>
                                                        <span class="block truncate">{{ props.option.name }}</span>
                                                    </template>
                                                </template>
    
                                                <!-- Plantilla para cada opción en la lista -->
                                                <template v-slot:selectOption="props">
                                                    <div :class="props.option.id === search.neighborhood_id ? 'font-semibold' : 'font-normal'" class="font-normal block truncate">
                                                        {{ props.option.name }}
                                                    </div>
                                                </template>
                                            </input-select>
                                        </div>
                                        <div v-if="errors.neighborhood_id" class="col-span-3">
                                            <p class="text-red-600 text-sm mt-2">{{ errors.neighborhood_id }}</p>
                                        </div>
                                    </div>
                                    <div class="col-span-3">
                                        <label class="block text-sm font-medium leading-5 text-gray-700" for="searchStreet">{{ $t('Ticket street') }}</label>
                                        <div class="mt-1 relative rounded-md shadow-sm">
                                            <input
                                                id="searchStreet"
                                                v-model="search.street"
                                                :placeholder="$t('Ticket street')"
                                                class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                                v-uppercase
                                            >
                                        </div>
                                        <div v-if="errors.street" class="col-span-3">
                                            <p class="text-red-600 text-sm mt-2">{{ errors.street }}</p>
                                        </div>
                                    </div>
                                    <div class="col-span-3">
                                        <label class="block text-sm font-medium leading-5 text-gray-700" for="searchLetter">{{ $t('Ticket letter') }}</label>
                                        <div class="mt-1 relative rounded-md shadow-sm">
                                            <input
                                                id="searchLetter"
                                                v-model="search.letter"
                                                :placeholder="$t('Ticket letter')"
                                                class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                                v-uppercase
                                            />
                                        </div>
                                        <div v-if="errors.letter" class="col-span-3">
                                            <p class="text-red-600 text-sm mt-2">{{ errors.letter }}</p>
                                        </div>
                                    </div>
                                    <div class="col-span-3">
                                        <label class="block text-sm font-medium leading-5 text-gray-700" for="searchNumber">{{ $t('Ticket number') }}</label>
                                        <div class="mt-1 relative rounded-md shadow-sm">
                                            <input
                                                id="searchNumber"
                                                v-model="search.number"
                                                :placeholder="$t('Ticket number')"
                                                class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                                v-uppercase    
                                            >
                                        </div>
                                        <div v-if="errors.number" class="col-span-3">
                                            <p class="text-red-600 text-sm mt-2">{{ errors.number }}</p>
                                        </div>
                                    </div>
                                    <div class="col-span-3">
                                        <label class="block text-sm font-medium leading-5 text-gray-700" for="searchLetter">{{ $t('Ticket letter') }}</label>
                                        <div class="mt-1 relative rounded-md shadow-sm">
                                            <input
                                                id="searchLetter"
                                                v-model="search.letter2"
                                                :placeholder="$t('Ticket letter')"
                                                class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                                v-uppercase
                                            />
                                        </div>
                                        <div v-if="errors.letter2" class="col-span-3">
                                            <p class="text-red-600 text-sm mt-2">{{ errors.letter2 }}</p>
                                        </div>
                                    </div>
                                    <div class="col-span-3">
                                        <label class="block text-sm font-medium leading-5 text-gray-700" for="crossovers">{{ $t('Crossover') }}</label>
                                        <div class="mt-1 relative rounded-md shadow-sm">
                                            <input
                                                id="crossovers"
                                                v-model="search.crossovers"
                                                :placeholder="$t('Crossover')"
                                                class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                                v-uppercase
                                            />
                                        </div>
                                        <div v-if="errors.crossovers" class="col-span-3">
                                            <p class="text-red-600 text-sm mt-2">{{ errors.crossovers }}</p>
                                        </div>
                                    </div>
                                    <div class="col-span-3">
                                        <label class="block text-sm font-medium leading-5 text-gray-700" for="searchLetter">{{ $t('Ticket letter') }}</label>
                                        <div class="mt-1 relative rounded-md shadow-sm">
                                            <input
                                                id="searchLetter"
                                                v-model="search.letter3"
                                                :placeholder="$t('Ticket letter')"
                                                class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                                v-uppercase
                                            />
                                        </div>
                                        <div v-if="errors.letter3" class="col-span-3">
                                            <p class="text-red-600 text-sm mt-2">{{ errors.letter3 }}</p>
                                        </div>
                                    </div>
                                    <div class="col-span-3">
                                        <label class="block text-sm font-medium leading-5 text-gray-700" for="crossovers2">{{ $t('Crossover2') }}</label>
                                        <div class="mt-1 relative rounded-md shadow-sm">
                                            <input
                                                id="crossovers2"
                                                v-model="search.crossovers2"
                                                :placeholder="$t('Crossover2')"
                                                class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                                v-uppercase
                                            />
                                        </div>
                                        <div v-if="errors.crossovers2" class="col-span-3">
                                            <p class="text-red-600 text-sm mt-2">{{ errors.crossovers2 }}</p>
                                        </div>
                                    </div>
                                    <div class="col-span-3">
                                        <label class="block text-sm font-medium leading-5 text-gray-700" for="searchLetter">{{ $t('Ticket letter') }}</label>
                                        <div class="mt-1 relative rounded-md shadow-sm">
                                            <input
                                                id="searchLetter"
                                                v-model="search.letter4"
                                                :placeholder="$t('Ticket letter')"
                                                class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                                v-uppercase
                                            />
                                        </div>
                                        <div v-if="errors.letter4" class="col-span-3">
                                            <p class="text-red-600 text-sm mt-2">{{ errors.letter4 }}</p>
                                        </div>
                                    </div>
                                    <div class="col-span-3">
                                        <label class="block text-sm font-medium leading-5 text-gray-700" for="referency">{{ $t('Referency') }}</label>
                                        <div class="mt-1 relative rounded-md shadow-sm">
                                            <input
                                                id="referency"
                                                v-model="search.referency"
                                                :placeholder="$t('Referency')"
                                                class="disabled:bg-gray-200 disabled:cursor-not-allowed form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                                v-uppercase
                                            >
                                        </div>
                                        <div v-if="errors.referency" class="col-span-3">
                                            <p class="text-red-600 text-sm mt-2">{{ errors.referency }}</p>
                                        </div>
                                    </div>
                                    <div class="col-span-3">
                                        <button type="submit" class="btn btn-blue">Buscar</button>
                                    </div>
                                </div>
                            </form>
                            <form id="ticketForm" @submit.prevent="saveTicket">
                                <div class="grid grid-cols-3 gap-6">
                                    <div class="col-span-3">
                                        <label class="block text-sm font-medium leading-5 text-gray-700" for="customerName">{{ $t('Name') }}</label>
                                        <div class="mt-1 relative rounded-md shadow-sm">
                                            <input
                                                id="customerName"
                                                v-model="ticket.customer_name"
                                                :placeholder="$t('Name')"
                                                class="disabled:bg-gray-200 disabled:cursor-not-allowed form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                                :disabled="ticket.customer_id"
                                                v-uppercase
                                            >
                                        </div>
                                        <div v-if="errors.customer_name" class="col-span-3">
                                            <p class="text-red-600 text-sm mt-2">{{ errors.customer_name }}</p>
                                        </div>
                                    </div>
                                    <div class="col-span-3">
                                        <label class="block text-sm font-medium leading-5 text-gray-700" for="customerPhone">{{ $t('Phone') }}</label>
                                        <div class="mt-1 relative rounded-md shadow-sm">
                                            <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                <span class="text-gray-500 sm:text-sm">+52</span>
                                            </div>
                                            <input
                                                id="customerPhone"
                                                v-model="ticket.customer_phone"
                                                :placeholder="$t('Phone')"
                                                class="disabled:bg-gray-200 disabled:cursor-not-allowed form-input block w-full pl-12 transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                                :disabled="ticket.customer_id"
                                                maxlength="10"
                                                v-uppercase
                                            >
                                        </div>
                                        <div v-if="errors.customer_phone" class="col-span-3">
                                            <p class="text-red-600 text-sm mt-2">{{ errors.customer_phone }}</p>
                                        </div>
                                    </div>
                                    <div class="col-span-3">
                                        <label class="block text-sm font-medium leading-5 text-gray-700" for="department">{{ $t('Department') }}</label>
                                        <div class="mt-1 relative rounded-md shadow-sm">
                                            <input-select
                                                id="department"
                                                v-model="ticket.department_id"
                                                :options="departmentList"
                                                option-label="name"
                                            />
                                        </div>
                                        <div v-if="errors.department_id" class="col-span-3">
                                            <p class="text-red-600 text-sm mt-2">{{ errors.department_id }}</p>
                                        </div>
                                    </div>
                                    <div class="col-span-3">
                                        <label class="block text-sm font-medium leading-5 text-gray-700" for="priority">{{ $t('Priority') }}</label>
                                        <div class="mt-1 relative rounded-md shadow-sm">
                                            <input-select
                                                id="priority"
                                                v-model="ticket.priority_id"
                                                :options="priorityList"
                                                option-label="name"
                                            />
                                        </div>
                                        <div v-if="errors.priority_id" class="col-span-3">
                                            <p class="text-red-600 text-sm mt-2">{{ errors.priority_id }}</p>
                                        </div>
                                    </div>
                                    <div class="col-span-3">
                                        <label class="block text-sm font-medium leading-5 text-gray-700" for="service_type">{{ $t('Service Type') }}</label>
                                        <div class="mt-1 relative rounded-md shadow-sm">
                                            <input-select
                                                id="service_type"
                                                v-model="ticket.service_type"
                                                :options="serviceTypes"
                                                option-label="name"
                                            />
                                        </div>
                                        <div v-if="errors.service_type" class="col-span-3">
                                            <p class="text-red-600 text-sm mt-2">{{ errors.service_type }}</p>
                                        </div>
                                    </div>
                                    <div class="col-span-3">
                                        <label class="block text-sm font-medium leading-5 text-gray-700" for="report_medium">{{ $t('Service Report Medium') }}</label>
                                        <div class="mt-1 relative rounded-md shadow-sm">
                                            <input-select
                                                id="report_medium"
                                                v-model="ticket.report_medium"
                                                :options="reportMedium"
                                                option-label="name"
                                            />
                                        </div>
                                        <div v-if="errors.report_medium" class="col-span-3">
                                            <p class="text-red-600 text-sm mt-2">{{ errors.report_medium }}</p>
                                        </div>
                                    </div>
                                    <div class="col-span-3">
                                        <label class="block text-sm font-medium leading-5 text-gray-700" for="file_upload">{{ $t('Service File') }}</label>
                                        <div class="mt-1 relative rounded-md shadow-sm">
                                            <input 
                                                type="file" 
                                                @change="handleFileUpload" 
                                                id="file_upload" 
                                                class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                            />
                                        </div>
                                    </div>
                                    <div class="col-span-3">
                                        <label class="block text-sm font-medium leading-5 text-gray-700" for="reportCommentary">{{ $t('Report commentary') }}</label>
                                        <div class="mt-1 relative rounded-md shadow-sm">
                                            <textarea
                                                id="reportCommentary"
                                                v-model="ticket.body"
                                                :placeholder="$t('Report commentary')"
                                                class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                                v-uppercase
                                            ></textarea>
                                        </div>
                                        <div v-if="errors.body" class="col-span-3">
                                            <p class="text-red-600 text-sm mt-2">{{ errors.body }}</p>
                                        </div>
                                    </div>
                                    <div v-if="ticket.attachments.length > 0" class="col-span-3">
                                        <div class="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-2">
                                            <template v-for="(attachment, index) in ticket.attachments">
                                                <attachment :details="attachment" v-on:remove="removeAttachment(index)"/>
                                            </template>
                                        </div>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                    <div class="bg-gray-100 text-right px-4 py-3 sm:px-6">
                        <div class="inline-flex">
                            <router-link
                                class="btn btn-secondary shadow-sm rounded-md mr-4"
                                to="/tickets"
                            >
                                {{ $t('Cancel') }}
                            </router-link>
                            <button
                                form="ticketForm"
                                class="btn btn-green shadow-sm rounded-md"
                                type="submit"
                            >
                                {{ $t('Create ticket') }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        <input ref="fileInput" hidden type="file" @change="uploadFile($event)">
    </main>
</template>

<script>
import { TheMask } from 'vue-the-mask';
import * as yup from 'yup';

export default {
    name: "new",
    components: {
        TheMask
    },
    metaInfo() {
        return {
            title: this.$i18n.t('Create ticket')
        }
    },
    data() {
        return {
            loading: {
                form: true,
                file: false,
            },
            uploadingFileProgress: 0,
            serviceTypes: [],
            reportMedium: [],
            customerList: [],
            neighborhoodList: [],
            reportTypes: [],
            search: {
                neighborhood_id: null,
                street: "",
                letter: "",
                letter2: "",
                letter3: "",
                letter4: "",
                number: "",
                mapUrl: "",
                type: "nomenclature",
                crossovers: null,
                crossovers2: null,
                referency: '',
            },
            ticket: {
                customer_id: null,
                customer_name: '',
                customer_email: '',
                customer_phone: '',
                customer_neighborhood_id: null,
                customer_letter2: "",
                customer_letter3: "",
                customer_letter4: "",
                crossovers: '',
                crossovers2: '',
                unit_number: '',
                referency: '',
                report_type_id: null,
                user_id: this.$store.state.user.id,
                subject: '',
                department_id: null,
                status_id: 1,
                priority_id: 1,
                body: '',
                street: '',
                letter: '',
                number: '',
                service_type: null,
                report_medium: null,
                file: null,
                attachments: [],
            },
            departmentList: [],
            userList: [],
            statusList: [],
            priorityList: [],
            cannedReplyList: [],
            errors: {}
        }
    },
    mounted() {
        this.getFilters();
        this.getCannedReplies();
    },
    watch: {
        search: {
            handler() {
                this.validateAllFieldsSearch();
            },
            deep: true
        },
        ticket: {
            handler() {
                this.validateAllFieldsTicket()
            },
            deep: true
        }
    },
    methods: {
        getFilters() {
            const self = this;
            self.loading.form = true;
            axios.get('api/dashboard/tickets/filters').then(function (response) {
                self.userList = response.data.customers;
                self.departmentList = response.data.departments;
                self.statusList = response.data.statuses;
                self.priorityList = response.data.priorities;
                self.serviceTypes = response.data.serviceTypes;
                self.reportMedium = response.data.reportMedium;
                self.neighborhoodList = response.data.neighborhoodList;
                self.reportTypes = response.data.reportTypes;
                self.loading.form = false;
            }).catch(function () {
                self.loading.form = false;
            })
        },
        async searchCustomer() {
            const self = this;
            await self.validateAllFieldsSearch();
            console.log(self.errors)
            if (Object.values(self.errors).some(error => error)) return;

            self.loading.form = true;
            
            axios.get('api/dashboard/tickets/search-customer', { params: this.search }).then(function (response) {
                const data = response.data;

                if(!data) {
                    self.ticket.customer_id = null;
                    self.ticket.customer_name = '';
                    self.ticket.customer_email = '';
                    self.ticket.customer_phone = '';
                    self.ticket.customer_neighborhood_id = null;
                    self.ticket.street = '';
                    self.ticket.letter = '';
                    self.ticket.number = '';
                    self.ticket.crossovers = '';
                    self.ticket.crossovers2 = ""
                    self.loading.form = false;
                    return
                }

                self.ticket.customer_id = data.id;
                self.ticket.customer_name = data.name;
                self.ticket.customer_email = data.email || '';
                self.ticket.customer_phone = data.phone;
                self.ticket.customer_neighborhood_id = data.neighborhood_id;
                self.ticket.street = data.street;
                self.ticket.letter = data.letter;
                self.ticket.number = data.number;
                self.ticket.crossovers = data.crossovers;
                self.ticket.crossovers2 = data.crossovers2;
                self.loading.form = false;
            }).catch(function () {
                self.loading.form = false;
            })
        },
        handleFileUpload(event) {
            this.ticket.file = event.target.files[0];
        },
        getCannedReplies() {
            const self = this;
            axios.get('api/dashboard/tickets/canned-replies').then(function (response) {
                self.cannedReplyList = response.data;
            })
        },
        async saveTicket() {
            const self = this;

            await self.validateAllFieldsSearch();
            await self.validateAllFieldsTicket();

            if (Object.values(self.errors).some(error => error)) return;

            self.loading.form = true;
            self.ticket.user_id = this.$store.state.user.id

            if(!self.ticket.customer_id) {
                self.ticket.street = self.search.street;
                self.ticket.letter = self.search.letter;
                self.ticket.customer_letter2 = self.search.letter2;
                self.ticket.customer_letter3 = self.search.letter3;
                self.ticket.customer_letter4 = self.search.letter4;
                self.ticket.number = self.search.number;
                self.ticket.customer_neighborhood_id = self.search.neighborhood_id;
                self.ticket.crossovers = self.search.crossovers;
                self.ticket.crossovers2 = self.search.crossovers2 || '';
                self.ticket.referency = self.search.referency || '';
            }

            const ticket = {... self.ticket};

            ticket.customer_phone = !ticket.customer_phone.startsWith('+52') ? `+52${ticket.customer_phone}` : ticket.customer_phone;

            if(!ticket.file) {
                delete ticket.file;
            }

            const formData = new FormData();

            for (const key in ticket) {
                if (ticket[key] instanceof Array) {
                    ticket[key].forEach((item, index) => {
                        formData.append(`${key}[${index}]`, item);
                    });
                } else {
                    formData.append(key, ticket[key]);
                }
            }

            axios.post('api/dashboard/tickets', formData).then(function (response) {
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: self.$i18n.t('Data saved correctly').toString(),
                    type: 'success'
                });
                self.$router.push('/tickets/' + response.data.ticket.uuid);
            }).catch(function () {
                self.loading.form = false;
            });
        },
        selectUploadFile() {
            if (!this.loading.file) {
                this.$refs.fileInput.click();
            } else {
                this.$notify({
                    title: this.$i18n.t('Error').toString(),
                    text: this.$i18n.t('A file is being uploaded').toString(),
                    type: 'warning'
                });
            }
        },
        uploadFile(e) {
            const self = this;
            const formData = new FormData();
            self.loading.file = true;
            formData.append('file', e.target.files[0]);
            axios.post(
                'api/dashboard/tickets/attachments',
                formData,
                {
                    headers: {'Content-Type': 'multipart/form-data'},
                    onUploadProgress: function (progressEvent) {
                        self.uploadingFileProgress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
                    }.bind(this)
                }
            ).then(function (response) {
                self.loading.file = false;
                self.uploadingFileProgress = 0;
                self.$refs.fileInput.value = null;
                self.ticket.attachments.push(response.data);
            }).catch(function () {
                self.loading.file = false;
                self.uploadingFileProgress = 0;
                self.$refs.fileInput.value = null;
            });
        },
        removeAttachment(attachment) {
            this.ticket.attachments.splice(attachment, 1);
        },
        async validateAllFieldsSearch() {
            const schema = yup.object().shape({
                    type: yup.string().required('Por favor selecciona un tipo de búsqueda.'),
                    mapUrl: yup.string().url('Por favor ingresa una URL válida.'),
                    neighborhood_id: yup.string().when('type', (type, schema) => {
                        if ( type[0] === 'nomenclature') {
                        return schema.required('Por favor selecciona un barrio.');
                        }
                        return schema.notRequired();
                    }),
                    
                    street: yup.string().when('type', (type, schema) => {
                        if ( type[0] === 'nomenclature') {
                        return schema.required('La calle es obligatoria.');
                        }
                        return schema.notRequired();
                    }),
                    
                    letter: yup.string(),
                    
                    number: yup.string().when('type', (type, schema) => {
                        if ( type[0] === 'nomenclature') {
                        return schema.required('El número es obligatorio.');
                        }
                        return schema.notRequired();
                    }),

                    crossovers: yup.string().when('type', (type, schema) => {
                        if ( type[0] === 'nomenclature') {
                            return schema.required('El cruzamiento 1 es obligatorio.');
                        }
                        return schema.notRequired();
                    }),

                    crossovers2: yup.string().nullable(),
            });

            try {
                Object.keys(this.errors).forEach(field => {
                    this.$set(this.errors, field, '');
                });
                await schema.validate(this.search, { abortEarly: false });
            } catch (validationErrors) {
                validationErrors.inner.forEach(error => {
                    this.$set(this.errors, error.path, error.message);
                });
            }
        },
        async validateAllFieldsTicket() {
            const ticketFormSchema = yup.object().shape({
                customer_name: yup.string().required('El nombre es obligatorio.'),
                customer_phone: yup.string()
                    .matches(/^(\+52)?[1-9][0-9]{9}$/, 'Número de teléfono no válido. Debe ser un número de 10 dígitos que comience con un número válido.')
                    .required('El teléfono es obligatorio.'),
                // customer_email: yup.string().required('El correo es obligatorio.').email('Debe ser un correo electrónico válido.'),
                // unit_number: yup.number('El numero de unidad debe ser un número').positive('El número de unidad debe ser positivo').typeError('Por favor ingrese un número válido.').required('El número de unidad es obligatoria.'),
                // subject: yup.string().required('El asunto es obligatorio.'),
                department_id: yup.string().required('El departamento es obligatorio.'),
                status_id: yup.string().required('El estado es obligatorio.'),
                priority_id: yup.string().required('La prioridad es obligatoria.'),
                service_type: yup.string().required('El tipo de servicio es obligatorio.'),
                report_medium: yup.string().required('El medio de reporte es obligatorio.'),
                body: yup.string().required('El comentario del reporte es obligatorio.'),
                referency: yup.string().nullable(),
            });

            try {
                await ticketFormSchema.validate(this.ticket, { abortEarly: false });
                Object.keys(this.errors).forEach(field => {
                    this.$set(this.errors, field, '');
                });
            } catch (validationErrors) {
                Object.keys(this.errors).forEach(field => {
                    this.$set(this.errors, field, '');
                });
                this.validateAllFieldsSearch()
                validationErrors.inner.forEach(error => {
                    this.$set(this.errors, error.path, error.message);
                });
            }
        }
    },
}
</script>
